import { Box, IconButton, InputBase, Typography } from '@mui/material';
import { MouseEvent, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableCellCandidateType, TableCellType } from 'entities/Files/types';
import { ColorPalette } from 'shared/consts/colorPalette';
import { getItemCode } from 'shared/helpers/getItemCode';
import { Icon } from 'shared/ui/Icon';
import { getCellSourceId } from 'components/file-editor-components/file-viewer/region-types';
import { CellTarget } from 'components/file-editor-components/table-fields/types';
import { cellStyle, resolveTableCellValue } from 'components/file-editor-components/table-fields/utils';

type TableCellProps = {
	tableIndex: number;
	rowIndex: number;
	columnIndex: number;
	cell: TableCellType | undefined;
	isSelected: boolean;
	isPendingTarget: boolean;
	canEditContent: boolean;
	drawMode?: boolean;
	canPickCandidate: boolean;
	hasSelectHandler: boolean;
	isPinned: boolean;
	pinnedLeftOffset: number | undefined;
	onSelectSource?: (sourceId: string | null) => void;
	onCellTargetClick?: (target: CellTarget) => void;
	onCellContentChange?: (target: CellTarget, content: string) => void;
	onCellCandidateSelect?: (target: CellTarget, candidate: TableCellCandidateType) => void;
	onCellDelete?: (target: CellTarget) => void;
};

const TableCell = memo(function TableCell({
	tableIndex,
	rowIndex,
	columnIndex,
	cell,
	isSelected,
	isPendingTarget,
	canEditContent,
	drawMode,
	canPickCandidate,
	hasSelectHandler,
	isPinned,
	pinnedLeftOffset,
	onSelectSource,
	onCellTargetClick,
	onCellContentChange,
	onCellCandidateSelect,
	onCellDelete,
}: TableCellProps) {
	const { t } = useTranslation();
	const [isPickingCandidate, setIsPickingCandidate] = useState(false);
	const { resolvedValue, displayValue, isUnresolvedMulti } = resolveTableCellValue(cell);

	const hasRegion = Boolean(cell?.boundingRegions?.length);
	const isFilled = Boolean(resolvedValue) || hasRegion;
	const canLinkRegion = Boolean(drawMode && onCellTargetClick && !hasRegion);
	const sourceId = getCellSourceId(tableIndex, rowIndex, columnIndex);

	const isMultiPicking = isUnresolvedMulti && isPickingCandidate;
	const showLinkHint = !isFilled && (canLinkRegion || isPendingTarget);
	const isEditable = !isUnresolvedMulti && !showLinkHint && canEditContent;
	const cellText = isUnresolvedMulti
		? displayValue
		: showLinkHint
		? t(isPendingTarget ? 'file_editor.draw_region_for_cell' : 'file_editor.click_to_link_region')
		: resolvedValue ?? '';
	const cellTextColor = isUnresolvedMulti ? ColorPalette.violetRed : ColorPalette.astronaut;
	const cursor = canLinkRegion
		? 'pointer'
		: cell?.status === 'MULTI'
		? canPickCandidate
			? 'pointer'
			: 'default'
		: isFilled
		? hasSelectHandler
			? 'pointer'
			: 'default'
		: 'default';

	const handleClick = () => {
		if (canLinkRegion) {
			onCellTargetClick?.({ tableIndex, rowIndex, columnIndex });
			return;
		}
		if (cell?.status === 'MULTI') {
			if (canPickCandidate) setIsPickingCandidate(true);
			return;
		}
		if (isFilled) onSelectSource?.(isSelected ? null : sourceId);
	};

	const handleDelete = (event: MouseEvent) => {
		event.stopPropagation();
		onCellDelete?.({ tableIndex, rowIndex, columnIndex });
	};

	const showDelete = isFilled && !isPickingCandidate && Boolean(onCellDelete);

	return (
		<Box
			component={'td'}
			data-source-id={sourceId}
			onClick={handleClick}
			sx={{
				...cellStyle,
				...(cell?.autocomplete || cell?.status === 'MULTI'
					? { borderColor: ColorPalette.jungleGreenTransparent }
					: {}),
				position: isPinned ? 'sticky' : 'relative',
				left: isPinned ? pinnedLeftOffset ?? 0 : undefined,
				zIndex: isPinned ? 1 : 0,
				cursor,
				backgroundColor: isSelected ? ColorPalette.zircon : isPinned ? ColorPalette.white : undefined,
				boxShadow: isSelected
					? `inset 0 0 0 1.5px ${ColorPalette.royalBlue}`
					: isPendingTarget
					? `inset 0 0 0 1.5px ${ColorPalette.violetRed}`
					: undefined,
				...(showDelete && {
					'&:hover .table-cell-delete': { opacity: 1 },
				}),
			}}
		>
			{showDelete && (
				<IconButton
					className={'table-cell-delete'}
					onClick={handleDelete}
					size={'small'}
					sx={{
						position: 'absolute',
						top: '2px',
						right: '2px',
						padding: '2px',
						opacity: 0,
						transition: 'opacity .15s',
						zIndex: 11,
					}}
				>
					<Icon
						id={'close_round'}
						color={ColorPalette.violetRed}
						style={{ width: '14px', height: '14px' }}
					/>
				</IconButton>
			)}
			{isMultiPicking ? (
				<select
					autoFocus
					value={cell?.manualOverride ? getItemCode(cell.manualOverride) : ''}
					onChange={(event) => {
						const candidate = cell?.itemCandidates?.find(
							(item) => getItemCode(item) === event.target.value,
						);
						if (candidate) {
							onCellCandidateSelect?.({ tableIndex, rowIndex, columnIndex }, candidate);
						}
						setIsPickingCandidate(false);
					}}
					onBlur={() => setIsPickingCandidate(false)}
				>
					<option value={''} disabled>
						{t('file_editor.choose_item_candidate')}
					</option>
					{cell?.itemCandidates?.map((candidate) => (
						<option key={getItemCode(candidate)} value={getItemCode(candidate)}>
							{getItemCode(candidate)} / {candidate.Description}
						</option>
					))}
				</select>
			) : isEditable ? (
				<InputBase
					value={resolvedValue ?? ''}
					onChange={(event) =>
						onCellContentChange?.({ tableIndex, rowIndex, columnIndex }, event.target.value)
					}
					sx={{ width: '100%', fontSize: '10px' }}
					inputProps={{ style: { padding: 0 } }}
					disabled={Boolean(cell?.assertionsData?.replaceVal || cell?.autocomplete)}
					multiline
				/>
			) : (
				<Typography variant={'overline1'} color={cellTextColor}>
					{cellText}
				</Typography>
			)}
		</Box>
	);
});

export default TableCell;
